import {
  LooplayIframeAuth,
  type EmitGameEventResponse,
  type GameEventKey,
} from "@looplay/sdk";

interface TelegramParentWindow extends Window {
  Telegram?: {
    WebApp?: unknown;
  };
}

const isTelegramMiniApp = () => {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, "")
  );
  const flagNames = ["telegramMiniApp", "telegram_mini_app"];
  const hasUrlFlag = flagNames.some(
    (key) => params.has(key) || hashParams.has(key)
  );

  if (hasUrlFlag) return true;
  if (window.parent === window) return false;

  try {
    const parentWindow = window.parent as TelegramParentWindow;
    return Boolean(parentWindow.Telegram?.WebApp);
  } catch {
    return false;
  }
};

const createLooplayAuth = () => {
  try {
    return new LooplayIframeAuth({
      parentOrigin: import.meta.env.VITE_PARENT_ORIGIN,
      // Required for builds published outside the Looplay iframe (own domain,
      // itch.io, ...) — get an appId from your creator dashboard. Inside the
      // Looplay iframe these fall back to what the parent pushes, so they're
      // optional there, but set them regardless.
      appId: import.meta.env.VITE_LOOPLAY_APP_ID,
      apiUrl: import.meta.env.VITE_LOOPLAY_API_URL,
      autoPlaytime: {
        enabled: true,
        intervalMs: 30_000,
        pauseOnHidden: true,
        pauseOnBlur: true,
      },
      autoRequestAds: {
        enabled: isTelegramMiniApp(),
        afterActionCount: 1,
        cooldownMs: 60_000,
      },
      onDebug: (message, details) => {
        if (details) {
          console.debug(`[Looplay] ${message}`, details);
          return;
        }

        console.debug(`[Looplay] ${message}`);
      },
    });
  } catch (error) {
    console.error("[Looplay] Failed to create LooplayIframeAuth", error);
    return null;
  }
};

type LooplayAuth = NonNullable<ReturnType<typeof createLooplayAuth>>;

let looplayAuth: LooplayAuth | null = createLooplayAuth();

const getLooplayAuth = () => {
  if (looplayAuth) return looplayAuth;
  looplayAuth = createLooplayAuth();
  return looplayAuth;
};

const logLooplayError = (operation: string, error: unknown) => {
  console.error(`[Looplay] ${operation} failed`, error);
};

const runLooplaySync = <T>(
  operation: string,
  callback: (auth: LooplayAuth) => T,
  fallback: T,
) => {
  const auth = getLooplayAuth();
  if (!auth) return fallback;

  try {
    return callback(auth);
  } catch (error) {
    logLooplayError(operation, error);
    return fallback;
  }
};

const runLooplayAsync = (
  operation: string,
  callback: (auth: LooplayAuth) => Promise<unknown> | unknown,
) => {
  const auth = getLooplayAuth();
  if (!auth) return;

  try {
    void Promise.resolve(callback(auth)).catch((error: unknown) => {
      logLooplayError(operation, error);
    });
  } catch (error) {
    logLooplayError(operation, error);
  }
};

const runLooplayPromise = <T>(
  operation: string,
  callback: (auth: LooplayAuth) => Promise<T> | T,
  fallback: T
) => {
  const auth = getLooplayAuth();
  if (!auth || !auth.getApiUrl()) return Promise.resolve(fallback);

  try {
    return Promise.resolve(callback(auth)).catch((error: unknown) => {
      logLooplayError(operation, error);
      return fallback;
    });
  } catch (error) {
    logLooplayError(operation, error);
    return Promise.resolve(fallback);
  }
};

export const getLooplayJwt = () =>
  runLooplaySync("getJwt", (auth) => auth.getJwt(), null);

export const getLooplayApiUrl = () =>
  runLooplaySync("getApiUrl", (auth) => auth.getApiUrl(), null);

export const getLooplayGameId = () =>
  runLooplaySync("getGameId", (auth) => auth.getGameId(), null);

export const canTrackLooplay = () =>
  runLooplaySync("canTrack", (auth) => auth.canTrack(), false);

export const subscribeLooplayAuth = (
  ...args: Parameters<LooplayAuth["subscribe"]>
) => runLooplaySync("subscribe", (auth) => auth.subscribe(...args), () => {});

export const requestLooplayAuth = () =>
  runLooplayAsync("requestAuth", (auth) => auth.requestAuth());

/** Call once when the game view opens, before any play/match tracking. */
export const trackLooplayView = () =>
  runLooplayAsync("trackView", (auth) => auth.trackView());

/** Call when the user presses Start Game to begin a new attempt and start auto playtime tracking. */
export const startLooplayAttempt = () =>
  runLooplayAsync("startAttempt", (auth) => {
    auth.startPlaySession();
  });

export const initializeLooplayAuth = () =>
  runLooplayAsync("init", (auth) => auth.init());

export const getLooplayClient = () =>
  runLooplaySync(
    "getClient",
    (auth) => (auth.getApiUrl() ? auth.getClient() : null),
    null
  );

const normalizePlayTimeSeconds = (playTimeSeconds?: number) => {
  if (playTimeSeconds === undefined) return undefined;
  if (!Number.isFinite(playTimeSeconds)) return undefined;
  return Math.max(0, Math.round(playTimeSeconds));
};

export type LooplayPlayTrackingType = "round" | "survival";

export interface ResolveTrackPlayTimeOptions {
  trackingType: LooplayPlayTrackingType;
  roundSeconds?: number;
  elapsedSeconds?: number;
  startedAtMs?: number;
  nowMs?: number;
}

export const resolveTrackPlayTimeSeconds = ({
  trackingType,
  roundSeconds,
  elapsedSeconds,
  startedAtMs,
  nowMs = Date.now(),
}: ResolveTrackPlayTimeOptions) => {
  if (trackingType === "round") {
    return normalizePlayTimeSeconds(roundSeconds ?? elapsedSeconds);
  }

  if (elapsedSeconds !== undefined) {
    return normalizePlayTimeSeconds(elapsedSeconds);
  }

  if (startedAtMs === undefined || !Number.isFinite(startedAtMs)) {
    return undefined;
  }

  const effectiveNow =
    typeof performance !== "undefined" && startedAtMs < 1e10 && nowMs > 1e11
      ? performance.now()
      : nowMs;

  return normalizePlayTimeSeconds((effectiveNow - startedAtMs) / 1000);
};

export const trackPlay = (playTimeSeconds?: number) =>
  runLooplayPromise(
    "trackPlay",
    (auth) => auth.trackPlay(normalizePlayTimeSeconds(playTimeSeconds)),
    false
  );

export const trackPlayByType = (options: ResolveTrackPlayTimeOptions) =>
  trackPlay(resolveTrackPlayTimeSeconds(options));

export const trackMatch = (
  matchId: string,
  options: { durationSeconds: number; isCompleted?: boolean; isWin?: boolean }
) =>
  runLooplayPromise("trackMatch", (auth) => auth.trackMatch(matchId, options), false);

export const emitGameEvent = (
  actionCode: GameEventKey | string,
  options?: {
    value?: number;
    refId?: string;
    payload?: Record<string, unknown>;
  }
): Promise<EmitGameEventResponse | null> => {
  const auth = getLooplayAuth();
  if (!auth || !auth.getApiUrl()) return Promise.resolve(null);

  try {
    return Promise.resolve(auth.emitGameEvent(actionCode, options)).catch(
      (error: unknown) => {
        logLooplayError("emitGameEvent", error);
        return null;
      }
    );
  } catch (error) {
    logLooplayError("emitGameEvent", error);
    return Promise.resolve(null);
  }
};

export const initializeLooplayLifecycleTracking = () =>
  runLooplayAsync("initLifecycleTracking", (auth) => auth.initLifecycleTracking());

export const completePlaySession = (playTimeSeconds?: number) =>
  runLooplayPromise(
    "completePlaySession",
    (auth) => auth.completePlaySession(normalizePlayTimeSeconds(playTimeSeconds)),
    false
  );

export const pausePlay = () =>
  runLooplaySync("pausePlay", (auth) => auth.pausePlay(), undefined);

export const resumePlay = () =>
  runLooplaySync("resumePlay", (auth) => auth.resumePlay(), undefined);

export const getPlayTimeSeconds = () =>
  runLooplaySync("getPlayTimeSeconds", (auth) => auth.getPlayTimeSeconds(), 0);

export const isPlayTimePaused = () =>
  runLooplaySync("isPlayTimePaused", (auth) => auth.isPlayTimePaused(), false);
